import { useState } from 'react'
import { Button, Modal, DateInput, Select } from '@/components/ui'
import {
  DOCUMENT_TYPE_OPTIONS,
  NO_DOCUMENT_TYPE,
  documentTypeLabel,
  documentTypeNeedsExpiry,
  missingExpiryMessage,
} from '../utils/documentTypes'

interface EditExpiryModalProps {
  isOpen: boolean
  onClose: () => void
  /**
   * expiryDate is an ISO timestamp or null to clear. documentType is only
   * passed when the type can be edited: a type, or null for none.
   */
  onConfirm: (expiryDate: string | null, documentType?: string | null) => void
  isPending?: boolean
  documentName?: string
  /** Current expiry as an ISO string (or null), used to seed the picker. */
  currentExpiry?: string | null
  /** Employee documents also say what they are (passport, visa…). */
  editType?: boolean
  currentType?: string | null
}

/** Convert an ISO timestamp/date to the yyyy-MM-dd value used by the date picker. */
function toDateInputValue(iso: string | null | undefined): string {
  if (!iso) return ''
  const ts = Date.parse(iso)
  if (Number.isNaN(ts)) return ''
  return new Date(ts).toISOString().slice(0, 10)
}

export function EditExpiryModal({
  isOpen,
  onClose,
  documentName,
  editType = false,
  ...formProps
}: EditExpiryModalProps): React.ReactElement {
  return (
    <Modal open={isOpen} onClose={onClose} title={editType ? 'Document details' : 'Set expiry date'}>
      {/* Mounted per opening, so each document starts from its own values. */}
      {isOpen ? (
        <ExpiryForm
          {...formProps}
          onClose={onClose}
          documentName={documentName}
          editType={editType}
        />
      ) : null}
    </Modal>
  )
}

function ExpiryForm({
  onClose,
  onConfirm,
  isPending = false,
  documentName,
  currentExpiry,
  editType = false,
  currentType,
}: Omit<EditExpiryModalProps, 'isOpen'>): React.ReactElement {
  const [value, setValue] = useState(() => toDateInputValue(currentExpiry))
  const [type, setType] = useState<string>(currentType ?? NO_DOCUMENT_TYPE)
  const [submitted, setSubmitted] = useState(false)

  // A candidate's free-text type stays selectable as it is.
  const typeOptions =
    currentType && !DOCUMENT_TYPE_OPTIONS.some((o) => o.value === currentType)
      ? [...DOCUMENT_TYPE_OPTIONS, { value: currentType, label: documentTypeLabel(currentType) ?? currentType }]
      : DOCUMENT_TYPE_OPTIONS
  const needsExpiry = editType && documentTypeNeedsExpiry(type)
  const expiryError = editType && submitted ? missingExpiryMessage(type, value) : null

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    setSubmitted(true)
    if (editType && missingExpiryMessage(type, value)) return
    // Send the date at end-of-day UTC so it stays "valid through" the chosen day.
    const expiry = value ? new Date(`${value}T23:59:59.999Z`).toISOString() : null
    if (editType) onConfirm(expiry, type || null)
    else onConfirm(expiry)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        {documentName ? (
          <>
            {editType ? 'What ' : 'Set the expiry date for '}
            <span className="font-medium text-gray-900 dark:text-gray-100">{documentName}</span>
            {editType ? ' is, and when it expires. ' : '. '}
          </>
        ) : editType ? (
          'What this document is, and when it expires. '
        ) : (
          'Set the expiry date for this document. '
        )}
        It applies to all files in this document group.
      </p>

      {editType && (
        <div>
          <label
            htmlFor="documentType"
            className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Type
          </label>
          <Select
            id="documentType"
            options={typeOptions}
            value={type}
            onChange={(next) => setType(next ?? NO_DOCUMENT_TYPE)}
            className="w-full"
          />
        </div>
      )}

      <div>
        <label
          htmlFor="documentExpiryDate"
          className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          Expiry date{' '}
          <span className="text-gray-400">{needsExpiry ? '(required)' : '(optional)'}</span>
        </label>
        <DateInput
          id="documentExpiryDate"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          aria-invalid={!!expiryError}
          aria-describedby={expiryError ? 'documentExpiryDateError' : undefined}
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
        />
        {expiryError ? (
          <p id="documentExpiryDateError" className="mt-1 text-xs text-danger" role="alert">
            {expiryError}
          </p>
        ) : null}
      </div>

      <div className="flex justify-end gap-2">
        {value && !needsExpiry && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setValue('')}
            loading={isPending}
          >
            Clear date
          </Button>
        )}
        <Button type="button" variant="secondary" size="sm" onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" size="sm" loading={isPending}>
          {isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  )
}

EditExpiryModal.displayName = 'EditExpiryModal'
